'use client';
import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { TicketStatusCard } from './TicketStatusCard';

/* ─── Search type config ──────────────────────────────────────────────────── */
const SEARCH_TYPES = [
    {
        key:         'ticketNumber',
        label:       'Ticket Number',
        icon:        'pi pi-ticket',
        placeholder: 'e.g. ENX-20250531-123456',
        hint:        'Ticket number from your confirmation email',
    },
    {
        key:         'email',
        label:       'Email Address',
        icon:        'pi pi-envelope',
        placeholder: 'you@example.com',
        hint:        'Email you used when raising the ticket',
    },
    {
        key:         'mobile',
        label:       'Mobile Number',
        icon:        'pi pi-phone',
        placeholder: '9876543210',
        hint:        'Mobile number provided when raising the ticket',
    },
];

/* ─── Fetch helper ────────────────────────────────────────────────────────── */
async function fetchTrackStatus(searchType, query) {
    const params = new URLSearchParams({ [searchType]: query.trim() });
    const res    = await fetch(`/api/support/track?${params.toString()}`);
    let json;
    try { json = await res.json(); } catch { json = {}; }
    return { ok: res.ok, status: res.status, data: json };
}

/* ─── No-results state ────────────────────────────────────────────────────── */
const NoResults = ({ query }) => (
    <div
        className="flex flex-column align-items-center gap-3 text-center border-round-xl p-5"
        style={{ background: '#f8faff', border: '1.5px dashed rgba(255,255,255,0.1)' }}
    >
        <div
            className="flex align-items-center justify-content-center border-circle"
            style={{ width: 56, height: 56, background: 'rgba(245,158,11,0.1)', border: '1.5px solid rgba(245,158,11,0.3)' }}
        >
            <i className="pi pi-search" style={{ fontSize: '1.3rem', color: '#f59e0b' }} />
        </div>
        <div>
            <p className="m-0 font-bold" style={{ fontSize: '1rem', color: '#0b3163' }}>No tickets found</p>
            <p className="m-0 mt-2" style={{ fontSize: '0.82rem', color: '#64748b', maxWidth: '340px' }}>
                No support ticket was found for <strong style={{ color: '#0b3163' }}>{query}</strong>.
                Please check the details and try again, or contact our support team.
            </p>
        </div>
        <a
            href="/contactus"
            style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                color: '#f59e0b', fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none',
            }}
        >
            Contact Support <i className="pi pi-arrow-right" style={{ fontSize: '0.65rem' }} />
        </a>
    </div>
);

/* ─── Results header ──────────────────────────────────────────────────────── */
const ResultsHeader = ({ count, query }) => (
    <div className="flex align-items-center justify-content-between gap-3 flex-wrap mb-4">
        <div className="flex align-items-center gap-2">
            <div
                className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                style={{ width: 34, height: 34, background: 'rgba(16,185,129,0.12)', border: '1.5px solid rgba(16,185,129,0.3)' }}
            >
                <i className="pi pi-check" style={{ fontSize: '0.8rem', color: '#10b981' }} />
            </div>
            <div>
                <p className="m-0 font-bold" style={{ fontSize: '0.92rem', color: '#0b3163' }}>
                    {count} ticket{count !== 1 ? 's' : ''} found
                </p>
                <p className="m-0" style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                    for "{query}"
                </p>
            </div>
        </div>
    </div>
);

/* ─── Main page ───────────────────────────────────────────────────────────── */
export const TrackStatusPage = () => {
    const router       = useRouter();
    const searchParams = useSearchParams();

    const [searchType, setSearchType] = useState('ticketNumber');
    const [query,      setQuery]      = useState('');
    const [loading,    setLoading]    = useState(false);
    const [results,    setResults]    = useState(null);   // null = not searched yet
    const [error,      setError]      = useState('');

    /* Auto-search if URL has ?q= param */
    useEffect(() => {
        const q = searchParams.get('q') || searchParams.get('ticketNumber');
        if (!q) return;
        setSearchType('ticketNumber');
        setQuery(q);
        doSearch('ticketNumber', q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const doSearch = async (type, value) => {
        const q = (value ?? query).trim();
        if (!q) return;
        setLoading(true);
        setError('');
        setResults(null);

        const { ok, status, data } = await fetchTrackStatus(type ?? searchType, q);

        setLoading(false);

        if (!ok) {
            if (status === 404) {
                setResults([]);
            } else {
                setError(data?.message || 'Something went wrong. Please try again.');
            }
            return;
        }

        const tickets = Array.isArray(data.data) ? data.data : [data.data];
        setResults(tickets.filter(Boolean));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        doSearch();
    };

    return (
        <main style={{ minHeight: '100vh', background: '#f8faff' }}>
            <style>{`
                .track-search-row {
                    display: flex;
                    gap: 0.75rem;
                }
                .track-search-row .track-submit-btn {
                    min-width: 110px;
                }
                @media (max-width: 767px) {
                    .track-search-row {
                        flex-direction: column;
                    }
                    .track-search-row .track-submit-btn {
                        width: 100%;
                        padding: 0.75rem 1rem !important;
                        min-width: unset !important;
                    }
                    .track-type-tabs {
                        display: grid !important;
                        grid-template-columns: repeat(3, 1fr);
                    }
                    .track-type-tabs button {
                        justify-content: center;
                        font-size: 0.7rem !important;
                    }
                    .track-hero-title {
                        font-size: 1.25rem !important;
                    }
                }
            `}</style>

            {/* ── Hero ─────────────────────────────────────────────────── */}
            <div
                style={{
                    background: 'var(--primary-color)',
                    padding:    '2rem 1rem 2.25rem',
                    position:   'relative',
                    overflow:   'hidden',
                }}
            >

                <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', padding: '0 1.5rem' }}>

                    {/* Back */}
                    <button
                        onClick={() => router.push('/support')}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.55rem 1.25rem', borderRadius: '0.7rem',
                            background: 'rgba(255,255,255,0.1)', border: '1.5px solid rgba(255,255,255,0.2)',
                            color: '#fff', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
                            backdropFilter: 'blur(6px)', marginBottom: '1.5rem',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                    >
                        <i className="pi pi-arrow-left" style={{ fontSize: '0.8rem' }} />
                        Back to Support
                    </button>

                    {/* Title */}
                    <div className="flex align-items-center gap-3 mb-3">
                        <div
                            className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                            style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)', backdropFilter: 'blur(4px)' }}
                        >
                            <i className="pi pi-search" style={{ fontSize: '1.4rem', color: '#fff' }} />
                        </div>
                        <div>
                            <h1 className="track-hero-title m-0 font-bold" style={{ fontSize: '1.6rem', color: '#fff', lineHeight: 1.2 }}>
                                Track Repair Status
                            </h1>
                            <p className="m-0 mt-1" style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.88rem' }}>
                                Enter your ticket number, email or mobile to see live status
                            </p>
                        </div>
                    </div>

                    {/* Feature chips */}
                    <div className="flex gap-2 flex-wrap">
                        {[
                            { icon: 'pi pi-bolt',    label: 'Real-time status' },
                            { icon: 'pi pi-lock',    label: 'No login needed'  },
                            { icon: 'pi pi-shield',  label: 'Repair tracking'  },
                        ].map(c => (
                            <span
                                key={c.label}
                                className="flex align-items-center gap-1"
                                style={{ background: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '999px', padding: '0.22rem 0.65rem', fontSize: '0.7rem', color: 'var(--primary-color)', fontWeight: 600 }}
                            >
                                <i className={c.icon} style={{ fontSize: '0.6rem', color: 'var(--primary-color)' }} />
                                {c.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Body ─────────────────────────────────────────────────── */}
            <section style={{ maxWidth: 720, margin: '0 auto', padding: '2.5rem 1.5rem' }}>

                {/* ── Search card ── */}
                <div
                    className="border-round-xl p-4 mb-5"
                    style={{ background: '#ffffff', border: '1.5px solid rgba(11,49,99,0.12)', boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}
                >
                    {/* Search type tabs */}
                    <div className="track-type-tabs flex gap-2 mb-4 flex-wrap">
                        {SEARCH_TYPES.map(t => (
                            <button
                                key={t.key}
                                onClick={() => { setSearchType(t.key); setResults(null); setError(''); }}
                                style={{
                                    display:      'flex',
                                    alignItems:   'center',
                                    gap:          '0.4rem',
                                    padding:      '0.45rem 0.85rem',
                                    borderRadius: '0.6rem',
                                    border:       searchType === t.key ? '1.5px solid var(--primary-color)' : '1.5px solid rgba(11,49,99,0.15)',
                                    background:   searchType === t.key ? 'var(--primary-color)' : 'transparent',
                                    color:        searchType === t.key ? '#fff' : 'var(--primary-color)',
                                    fontSize:     '0.78rem',
                                    fontWeight:   700,
                                    cursor:       'pointer',
                                    transition:   'all 0.2s',
                                }}
                            >
                                <i className={t.icon} style={{ fontSize: '0.68rem' }} />
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Search form */}
                    <form onSubmit={handleSubmit}>
                        {SEARCH_TYPES.filter(t => t.key === searchType).map(t => (
                            <div key={t.key}>
                                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: 'var(--primary-color)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
                                    {t.label}
                                </label>
                                <div className="track-search-row">
                                    <div style={{ position: 'relative', flex: 1 }}>
                                        <i
                                            className={t.icon}
                                            style={{ position: 'absolute', left: '0.9rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', color: 'var(--primary-color)', pointerEvents: 'none' }}
                                        />
                                        <input
                                            type={t.key === 'email' ? 'email' : t.key === 'mobile' ? 'tel' : 'text'}
                                            inputMode={t.key === 'mobile' ? 'tel' : undefined}
                                            value={query}
                                            onChange={e => setQuery(e.target.value)}
                                            placeholder={t.placeholder}
                                            style={{
                                                width:        '100%',
                                                padding:      '0.75rem 1rem 0.75rem 2.5rem',
                                                borderRadius: '0.7rem',
                                                border:       '1.5px solid rgba(11,49,99,0.2)',
                                                background:   '#fff',
                                                color:        'var(--primary-color)',
                                                fontSize:     '0.9rem',
                                                outline:      'none',
                                                boxSizing:    'border-box',
                                                transition:   'border-color 0.2s',
                                                fontFamily:   'inherit',
                                            }}
                                            onFocus={e  => { e.target.style.borderColor = 'var(--primary-color)'; }}
                                            onBlur={e   => { e.target.style.borderColor = 'rgba(11,49,99,0.2)'; }}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading || !query.trim()}
                                        className="track-submit-btn"
                                        style={{
                                            background:   loading || !query.trim() ? 'rgba(11,49,99,0.08)' : 'var(--primary-color)',
                                            border:       'none',
                                            borderRadius: '0.7rem',
                                            padding:      '0 1.5rem',
                                            fontSize:     '0.88rem',
                                            fontWeight:   700,
                                            color:        loading || !query.trim() ? 'rgba(11,49,99,0.35)' : '#fff',
                                            cursor:       loading || !query.trim() ? 'not-allowed' : 'pointer',
                                            display:      'flex',
                                            alignItems:   'center',
                                            gap:          '0.4rem',
                                            whiteSpace:   'nowrap',
                                            boxShadow:    loading || !query.trim() ? 'none' : '0 4px 16px rgba(11,49,99,0.3)',
                                            transition:   'all 0.2s',
                                            minWidth:     '110px',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {loading ? (
                                            <><i className="pi pi-spin pi-spinner" style={{ fontSize: '0.85rem' }} /> Searching…</>
                                        ) : (
                                            <><i className="pi pi-search" style={{ fontSize: '0.82rem' }} /> Search</>
                                        )}
                                    </button>
                                </div>
                                <p style={{ margin: '0.4rem 0 0', fontSize: '0.68rem', color: '#94a3b8' }}>
                                    <i className="pi pi-info-circle mr-1" style={{ fontSize: '0.62rem' }} />
                                    {t.hint}
                                </p>
                            </div>
                        ))}
                    </form>
                </div>

                {/* API error */}
                {error && (
                    <div
                        className="flex align-items-center gap-2 border-round-lg px-4 py-3 mb-4"
                        style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: '0.85rem' }}
                    >
                        <i className="pi pi-exclamation-circle" style={{ fontSize: '1rem', flexShrink: 0 }} />
                        <span>{error}</span>
                    </div>
                )}

                {/* Results */}
                {results !== null && (
                    results.length === 0 ? (
                        <NoResults query={query} />
                    ) : (
                        <>
                            <ResultsHeader count={results.length} query={query} />
                            <div className="flex flex-column gap-4">
                                {results.map((ticket, i) => (
                                    <TicketStatusCard key={ticket._id || ticket.ticketNumber || i} ticket={ticket} />
                                ))}
                            </div>
                        </>
                    )
                )}

                {/* Initial state hint */}
                {results === null && !loading && !error && (
                    <div
                        className="flex align-items-start gap-3 border-round-xl p-4"
                        style={{ background: 'var(--primary-color)', border: 'none' }}
                    >
                        <i className="pi pi-info-circle" style={{ fontSize: '1.1rem', color: '#fff', marginTop: '0.1rem', flexShrink: 0 }} />
                        <div>
                            <p className="m-0 font-bold" style={{ fontSize: '0.88rem', color: '#fff' }}>
                                How to track your repair
                            </p>
                            <p className="m-0 mt-1" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
                                Use the <strong style={{ color: '#fff' }}>ticket number</strong> from your confirmation email (e.g. <code style={{ color: '#fff', background: 'rgba(255,255,255,0.15)', padding: '0.1rem 0.35rem', borderRadius: '4px', fontSize: '0.78rem' }}>ENX-20250531-123456</code>).
                                You can also search by the <strong style={{ color: '#fff' }}>email</strong> or <strong style={{ color: '#fff' }}>mobile number</strong> used when raising the ticket.
                            </p>
                            <div className="flex gap-3 flex-wrap mt-3">
                                <a
                                    href="/warranty-support"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: '#fff', fontWeight: 700, fontSize: '0.78rem', textDecoration: 'none', background: 'rgba(255,255,255,0.15)', padding: '0.3rem 0.75rem', borderRadius: '999px' }}
                                >
                                    <i className="pi pi-shield" style={{ fontSize: '0.65rem' }} />
                                    Check warranty status
                                </a>
                                <a
                                    href="/contactus"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: 'rgba(255,255,255,0.75)', fontWeight: 700, fontSize: '0.78rem', textDecoration: 'none' }}
                                >
                                    <i className="pi pi-send" style={{ fontSize: '0.65rem' }} />
                                    Contact support
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </main>
    );
};
